<template>
  <div
    class="fe-flex fe-flex-col fe-border-b fe-border-solid fe-border-primary-grey-40 last:fe-border-b-0"
    ref="reviewList"
  >
    <div class="fe-flex fe-justify-between fe-gap-base fe-mb-xs">
      <div>
        <div class="fe-flex fe-items-center fe-gap-xs fe-mb-xs">
          <StarRating
            :read-only="true"
            :initial-rating="review.rating"
            smSize="s"
            lgSize="m"
          />
          <JdsBadge
            v-if="showApprovalPending"
            size="small"
            bg-color="fe-bg-marigold-50"
          >
            Approval Pending
          </JdsBadge>
          <VerifiedPurchase v-if="review.verified">
            Verified Purchase
          </VerifiedPurchase>
        </div>

        <div>
          <CustomerName
            :customer-name="review.customerName"
            :created-by="review.createdBy"
            :created-at="review.createdAt"
          />
        </div>
      </div>
      <div v-if="!isUserOwnReview && !config.reortAbuse?.hidden">
        <JdsIcon
          name="MoreVerticalIcon"
          smSize="s"
          lgSize="m"
          color="fe-text-primary-grey-80"
        />
      </div>
    </div>

    <div
      v-if="!config.reviewImage?.hidden"
      class="fe-overflow-x-auto fe-no-scrollbar fe-flex-shrink-0"
    >
      <ReviewImages
        :review="review"
        class="fe-mb-xs"
        @click="handleImageClick"
      />
    </div>

    <div class="fe-mb-xxs">
      <ReviewTitle :title="review.title" />
    </div>

    <div class="fe-mb-xs fe-overflow-y-auto fe-flex-grow fe-h-full">
      <ReviewContent
        ref="textContent"
        :content="review.content"
        :is-expanded="isExpanded"
        :hideReadMoreLessButton="hideReadMoreLessButton"
      />
    </div>

    <div class="fe-flex fe-items-end fe-justify-between fe-mt-auto">
      <div>
        <ReadMoreLess
          v-if="showReadMoreLessButton"
          @toggle="toggleExpandText"
          :is-expanded="isExpanded"
        />
      </div>
      <div class="fe-flex">
        <ReviewVote v-if="isReviewVoteVisible()" :review="review" />
      </div>
    </div>
  </div>
</template>

<script>
  import StarRating from "@/components/DesignSystem/StarRating/StarRating.vue"
  import ReviewVote from "./ReviewListItemComponent/ReviewVote.vue"
  import VerifiedPurchase from "./ReviewListItemComponent/VerifiedPurchase.vue"
  import CustomerName from "./ReviewListItemComponent/CustomerName.vue"
  import ReviewTitle from "./ReviewListItemComponent/ReviewTitle.vue"
  import ReviewContent from "./ReviewListItemComponent/ReviewContent.vue"
  import ReadMoreLess from "./ReviewListItemComponent/ReadMoreLess.vue"
  import ReviewImages from "./ReviewListItemComponent/ReviewImages.vue"
  import JdsBadge from "@/components/DesignSystem/JdsBadge/JdsBadge.vue"
  import GalleryContentModal from "@/components/ReviewAndRating/Gallery/GalleryContentModal.vue"

  import { DEFAULT_REVIEW_TRUNCATE_LINES } from "@/constants/constants"
  import JdsIcon from "@/components/DesignSystem/JdsIcon/JdsIcon.vue"
  import { modalMixin } from "@/mixins/modalMixin"

  export default {
    name: "ReviewListItem",
    components: {
      StarRating,
      ReviewVote,
      VerifiedPurchase,
      CustomerName,
      ReviewTitle,
      ReviewContent,
      ReadMoreLess,
      ReviewImages,
      JdsBadge,
      JdsIcon,
    },
    inject: ["globalState"], // top parent component state
    mixins: [modalMixin],
    props: {
      review: {
        type: Object,
        default: () => {},
        required: true,
      },
      showApprovalPending: {
        type: Boolean,
        default: false,
      },
      // This prop is used only when User's own review is being shown. In that case, we don't need to show upvote/downvote etc
      isUserOwnReview: {
        type: Boolean,
        default: false,
      },
      // This prop is used when review is being shown in Desktop Content Modal. Since we have lot of space in modal, no need to show Read More/Less Button
      hideReadMoreLessButton: {
        type: Boolean,
        default: false,
      },
      // This props is used when review is being show in Desktop Content Modal. This is for styling purpose only
      isLgContentModal: {
        type: Boolean,
        default: false,
      },
      // This props is used when review is being show in Mobile Content Modal. This is for styling purpose only
      isSmContentModal: {
        type: Boolean,
        default: false,
      },
      displayContext: {
        type: String,
        default: "list",
      },
    },
    data() {
      return {
        showReadMoreLessButton: false,
        isExpanded: this.hideReadMoreLessButton,
      }
    },
    mounted() {
      this.checkTextOverflow()
    },
    computed: {
      config() {
        return this.globalState.widgetConfig?.layout
      },
    },
    methods: {
      toggleExpandText() {
        this.isExpanded = !this.isExpanded
      },
      checkTextOverflow() {
        if (this.hideReadMoreLessButton) {
          this.showReadMoreLessButton = false

          return
        }

        this.$nextTick(() => {
          const element = this.$refs.textContent?.getElement()

          if (element) {
            const lineHeight = parseFloat(getComputedStyle(element).lineHeight)
            // If content is taking more than 3 line space, then truncate it and show Read More/Less Button
            const maxHeight = lineHeight * DEFAULT_REVIEW_TRUNCATE_LINES

            this.showReadMoreLessButton = element.scrollHeight > maxHeight
          }
        })
      },
      isReviewVoteVisible() {
        return !(
          this.isUserOwnReview ||
          this.globalState.user.user_id === this.review?.createdBy
        )
      },
      handleImageClick(imageId) {
        if (this.displayContext === "content_modal") {
          this.$emit("click", {
            currentReview: this.review,
            currentImageId: imageId,
          })
        } else {
          this.openModal(GalleryContentModal, {
            currentReview: this.review,
            currentImageId: imageId,
            showCurrentReviewOnly: true,
          })
        }
      },
    },
  }
</script>
